import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useNavigation } from '@/hooks/useNavigation';
import HomeScreen from '@/components/screens/HomeScreen';
import GameScreen from '@/components/screens/GameScreen';
import ShopScreen from '@/components/screens/ShopScreen';
import LevelSelectScreen from '@/components/screens/LevelSelectScreen';
import SettingsScreen from '@/components/screens/SettingsScreen';
import ProfileScreen from '@/components/screens/ProfileScreen';
import TestScreen from '@/components/screens/TestScreen';
import TestHubScreen from '@/components/screens/TestHubScreen';
import DesignSystemScreen from '@/components/screens/DesignSystemScreen';
import CharactersScreen from '@/components/screens/CharactersScreen';
import WorldMapScreen from '@/components/screens/WorldMapScreen';
import UpgradesScreen from '@/components/screens/UpgradesScreen';
import EntityCompareScreen from '@/components/screens/EntityCompareScreen';
import Game3DScreen from '@/components/screens/Game3DScreen';
import ThreeDShowcaseScreen from '@/components/screens/ThreeDShowcaseScreen';
import { StatsOverlay } from '@/components/ui';
import ScreenTransition, { ScreenContainer, BackgroundTransition } from '@/components/layout/ScreenTransition';
import { useSound } from '@/contexts/SoundContext';
import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

interface AppRoutesProps {
  onOpenSettings: () => void;
}

export function AppRoutes({ onOpenSettings }: AppRoutesProps) {
  const location = useLocation();
  const { startLobbyMusic } = useSound();

  // Play lobby music on all non-game screens
  useEffect(() => {
    if (location.pathname !== '/game' && location.pathname !== '/2d') {
      startLobbyMusic();
    }
  }, [location.pathname, startLobbyMusic]);

  return (
    <ScreenTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomeScreenWrapper onOpenSettings={onOpenSettings} />} />
        <Route path="/game" element={<Game3DScreenWrapper />} />
        <Route path="/2d" element={<GameScreenWrapper />} />
        <Route path="/shop" element={<ShopScreenWrapper />} />
        <Route path="/worlds" element={<WorldMapScreenWrapper />} />
        <Route path="/upgrades" element={<UpgradesScreenWrapper />} />
        <Route path="/levels" element={<LevelSelectScreenWrapper />} />
        <Route path="/settings" element={<SettingsScreenWrapper />} />
        <Route path="/profile" element={<ProfileScreenWrapper />} />
        <Route path="/test" element={<TestHubScreenWrapper />} />
        <Route path="/test/visual-styles" element={<TestScreenWrapper />} />
        <Route path="/test/design-system" element={<DesignSystemScreenWrapper />} />
        <Route path="/test/entity-compare" element={<EntityCompareScreenWrapper />} />
        <Route path="/test/3d-showcase" element={<ThreeDShowcaseScreenWrapper />} />
        <Route path="/characters" element={<CharactersScreenWrapper />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ScreenTransition>
  );
}

function HomeScreenWrapper({ onOpenSettings }: { onOpenSettings: () => void }) {
  const { goToGame, goToShop, goToLevelSelect, goToProfile } = useNavigation();
  
  return (
    <ScreenContainer layoutId="home-screen">
      <BackgroundTransition theme="home" animated={true} />
      <HomeScreen
        onStartGame={goToGame}
        onOpenSettings={onOpenSettings}
        onOpenShop={goToShop}
        onLevelSelect={goToLevelSelect}
        onOpenProfile={goToProfile}
      />
    </ScreenContainer>
  );
}

function GameScreenWrapper() {
  const { goHome, goToShop, goToLevelSelect, goToSettings } = useNavigation();
  const [statsOverlayOpen, setStatsOverlayOpen] = useState(false);
  
  const handleToggleStats = () => {
    setStatsOverlayOpen(prev => !prev);
  };
  
  return (
    <ScreenContainer layoutId="game-screen">
      <BackgroundTransition theme="game" animated={true} performance="low" />
      <GameScreen
        onOpenSettings={goToSettings}
        onGoHome={goHome}
        onOpenShop={goToShop}
        onToggleStats={handleToggleStats}
        onLevelSelect={goToLevelSelect}
      />
      <StatsOverlay 
        isOpen={statsOverlayOpen} 
        onClose={() => setStatsOverlayOpen(false)} 
      />
    </ScreenContainer>
  );
}

function Game3DScreenWrapper() {
  const { goHome, goToShop, goToLevelSelect, goToSettings } = useNavigation();
  const [statsOverlayOpen, setStatsOverlayOpen] = useState(false);

  const handleToggleStats = () => {
    setStatsOverlayOpen(prev => !prev);
  };

  return (
    <ScreenContainer layoutId="game3d-screen">
      <Game3DScreen
        onOpenSettings={goToSettings}
        onGoHome={goHome}
        onOpenShop={goToShop}
        onToggleStats={handleToggleStats}
        onLevelSelect={goToLevelSelect}
      />
      <StatsOverlay
        isOpen={statsOverlayOpen}
        onClose={() => setStatsOverlayOpen(false)}
      />
    </ScreenContainer>
  );
}

function ShopScreenWrapper() {
  const { goHome } = useNavigation();
  
  return (
    <ScreenContainer layoutId="shop-screen">
      <BackgroundTransition theme="shop" animated={true} />
      <ShopScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function WorldMapScreenWrapper() {
  const { goHome, goToGame, goToShop } = useNavigation();

  return (
    <ScreenContainer layoutId="world-map-screen">
      <BackgroundTransition theme="levels" animated={true} />
      <WorldMapScreen
        onGoHome={goHome}
        onStartLevel={(level) => goToGame(level)}
        onOpenShop={goToShop}
      />
    </ScreenContainer>
  );
}

function UpgradesScreenWrapper() {
  const { goHome, goToShop } = useNavigation();

  return (
    <ScreenContainer layoutId="upgrades-screen">
      <BackgroundTransition theme="shop" animated={true} />
      <UpgradesScreen onGoHome={goHome} onOpenShop={goToShop} />
    </ScreenContainer>
  );
}

function LevelSelectScreenWrapper() {
  const { goHome, goToGame } = useNavigation();
  
  return (
    <ScreenContainer layoutId="level-select-screen">
      <BackgroundTransition theme="levels" animated={true} />
      <LevelSelectScreen
        onStartLevel={(level) => goToGame(level)}
        onGoHome={goHome}
      />
    </ScreenContainer>
  );
}

function SettingsScreenWrapper() {
  const { goHome } = useNavigation();
  
  return (
    <ScreenContainer layoutId="settings-screen">
      <SettingsScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function ProfileScreenWrapper() {
  const { goHome } = useNavigation();
  
  return (
    <ScreenContainer layoutId="profile-screen">
      <ProfileScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function TestHubScreenWrapper() {
  const { goHome } = useNavigation();

  return (
    <ScreenContainer layoutId="test-hub-screen">
      <TestHubScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function TestScreenWrapper() {
  const { goHome } = useNavigation();

  return (
    <ScreenContainer layoutId="test-screen">
      <TestScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function DesignSystemScreenWrapper() {
  const { goHome } = useNavigation();

  return (
    <ScreenContainer layoutId="design-system-screen">
      <DesignSystemScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function EntityCompareScreenWrapper() {
  const { goHome } = useNavigation();

  return (
    <ScreenContainer layoutId="entity-compare-screen">
      <EntityCompareScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function ThreeDShowcaseScreenWrapper() {
  const { goHome } = useNavigation();

  return (
    <ScreenContainer layoutId="3d-showcase-screen">
      <ThreeDShowcaseScreen onGoHome={goHome} />
    </ScreenContainer>
  );
}

function CharactersScreenWrapper() {
  return (
    <ScreenContainer layoutId="characters-screen">
      <CharactersScreen />
    </ScreenContainer>
  );
}
